import { adminRequest } from './admin-api.js';

// Order types (mirrors payments-api DTOs)
export type OrderStatus =
  | 'PENDING'
  | 'REQUIRES_ACTION'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELLED'
  | 'REFUNDED'
  | 'PARTIALLY_REFUNDED';

export type SourceType = 'BOOKING' | 'TOURNAMENT_ENTRY' | 'INVOICE' | 'SUBSCRIPTION' | 'UNKNOWN';

export interface PaymentOrder {
  id: string;
  tenantId?: string | null;
  clubId?: string | null;
  sourceType: SourceType;
  sourceId?: string | null;
  currency: string;
  amount: number;
  feeAmount?: number | null;
  description?: string | null;
  status: OrderStatus;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentIntent {
  id: string;
  provider: string;
  externalRef?: string | null;
  checkoutUrl?: string | null;
  status: OrderStatus;
  amount: number;
  currency: string;
  createdAt: string;
}

export interface PaymentTransaction {
  id: string;
  provider: string;
  type: string;
  status: OrderStatus;
  amount: number;
  currency: string;
  externalRef?: string | null;
  occurredAt: string;
}

export interface PaymentOrderDetail extends PaymentOrder {
  intents: PaymentIntent[];
  transactions: PaymentTransaction[];
}

export interface ListOrdersResponse {
  data: PaymentOrder[];
  total: number;
  skip: number;
  take: number;
}

export interface ListOrdersQuery {
  tenantId?: string;
  clubId?: string;
  sourceType?: SourceType;
  sourceId?: string;
  status?: OrderStatus;
  skip?: number;
  take?: number;
}

const buildQueryString = (params: ListOrdersQuery): string => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
};

export type FulfillmentStatus = 'PENDING' | 'PROCESSING' | 'SHIPPED' | 'DELIVERED' | 'RETURNED';

export interface RefundRequest {
  amount?: number; // If not provided, full refund
  reason?: string;
}

export interface RefundResponse {
  id: string;
  orderId: string;
  amount: number;
  currency: string;
  status: string;
  refundedAt: string;
}

export interface CaptureResponse {
  id: string;
  orderId: string;
  amount: number;
  currency: string;
  status: string;
  capturedAt: string;
}

export interface FulfillmentUpdate {
  status: FulfillmentStatus;
  trackingNumber?: string;
  trackingUrl?: string;
  carrier?: string;
  notes?: string;
}

export interface FulfillmentResponse {
  id: string;
  orderId: string;
  status: FulfillmentStatus;
  trackingNumber?: string;
  trackingUrl?: string;
  carrier?: string;
  notes?: string;
  updatedAt: string;
}

export interface ExportOptions {
  format: 'csv' | 'json' | 'xlsx';
  dateFrom?: string;
  dateTo?: string;
  status?: OrderStatus;
  includeTransactions?: boolean;
}

export const ordersAdminService = {
  async listOrders(query: ListOrdersQuery = {}): Promise<ListOrdersResponse> {
    const qs = buildQueryString(query);
    return adminRequest<ListOrdersResponse>(`/payments/orders${qs}`);
  },

  async getOrder(orderId: string): Promise<PaymentOrderDetail> {
    return adminRequest<PaymentOrderDetail>(`/payments/orders/${orderId}`);
  },

  async cancelOrder(orderId: string): Promise<{ id: string; status: string; cancelledAt: string }> {
    return adminRequest(`/payments/orders/${orderId}/cancel`, { method: 'POST' });
  },

  /**
   * Capture payment for orders with REQUIRES_ACTION status (manual capture flow)
   */
  async capturePayment(orderId: string, amount?: number): Promise<CaptureResponse> {
    return adminRequest<CaptureResponse>(`/payments/orders/${orderId}/capture`, {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  },

  /**
   * Process refund for SUCCEEDED orders
   */
  async refundOrder(orderId: string, request: RefundRequest = {}): Promise<RefundResponse> {
    return adminRequest<RefundResponse>(`/payments/orders/${orderId}/refund`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  /**
   * Update order fulfillment status (shipped, delivered, etc.)
   */
  async updateFulfillment(orderId: string, update: FulfillmentUpdate): Promise<FulfillmentResponse> {
    return adminRequest<FulfillmentResponse>(`/payments/orders/${orderId}/fulfillment`, {
      method: 'PATCH',
      body: JSON.stringify(update),
    });
  },

  /**
   * Export orders for ERP/accounting integration
   */
  async exportOrders(options: ExportOptions): Promise<Blob> {
    const params = new URLSearchParams();
    params.set('format', options.format);
    if (options.dateFrom) params.set('dateFrom', options.dateFrom);
    if (options.dateTo) params.set('dateTo', options.dateTo);
    if (options.status) params.set('status', options.status);
    if (options.includeTransactions) params.set('includeTransactions', 'true');

    const response = await fetch(`/api/admin/payments/orders/export?${params.toString()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('admin_jwt') || ''}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to export orders');
    }

    return response.blob();
  },

  /**
   * Get export download URL (for large exports processed async)
   */
  async getExportUrl(exportId: string): Promise<{ url: string; expiresAt: string }> {
    return adminRequest<{ url: string; expiresAt: string }>(`/payments/orders/export/${exportId}`);
  },
};
